import React, { FunctionComponent } from 'react';

import { connect, useDispatch, useSelector } from 'react-redux';
import AppState from '@webapp/store/state';

import { Translate } from 'react-localize-redux';
import { rgba } from 'polished';
import styled, { css } from 'styled-components';
import { AnimatePresence, motion } from 'framer-motion';

import Typography from '@components/basics/typography';
import CompletedIcon from '@components/basics/icons/achievements-icons/completed';
import CircleRightArrowIcon from '@components/basics/icons/circle-right-arrow';

// Icons
import IdIcon from '@components/basics/icons/achievements-icons/id';
import UserIcon from '@components/basics/icons/achievements-icons/user';
import StarIcon from '@components/basics/icons/achievements-icons/star';
import MusicIcon from '@components/basics/icons/achievements-icons/music';
import MoneyIcon from '@components/basics/icons/achievements-icons/money';
import PhoneIcon from '@components/basics/icons/achievements-icons/phone';
import LaptopIcon from '@components/basics/icons/achievements-icons/laptop';
import LayoutIcon from '@components/basics/icons/achievements-icons/layout';
import { push } from 'connected-react-router';
import { OpenMenu } from '@webapp/store/ui/action-creators';

export type AchievementIconType =
  | 'laptop'
  | 'id'
  | 'music'
  | 'layout'
  | 'user'
  | 'money'
  | 'phone'
  | 'star'
  | 'verify';

export interface AchievementItemType {
  icon: AchievementIconType;
  title: string;
  subtitle: string;
  isCompleted?: boolean;
  to: string;
  achievementKey: string;
  id?: string;
}

interface AchievementItemProps extends AchievementItemType {
  className?: string;
  isMobile: boolean;
  isFrontendCopy: boolean;
  openDistribution: () => void;
}

const mapStateToProps = (state: AppState) => ({
  isMobile: state.ui.mobile,
});

const AchievementItem: FunctionComponent<AchievementItemProps> = ({
  className,
  icon,
  title,
  subtitle,
  isCompleted,
  isMobile,
  to,
  isFrontendCopy,
  achievementKey,
  openDistribution,
}) => {
  const dispatch = useDispatch();
  const [isHovered, setHover] = React.useState(false);

  const artistId =
    useSelector((state: AppState) => state.artist.active?.id) || '';
  const isDistributionProPlan = useSelector((state: AppState) =>
    state.subscriptionManager.summary?.artists
      .find((artist) => artist.id === artistId)
      ?.subscriptions.some(
        (subscription) =>
          ['distribution', 'trac-pro'].includes(subscription.type) &&
          subscription.plan.includes('Pro')
      )
  );

  const getIcon = (icon: AchievementIconType) => {
    switch (icon) {
      case 'laptop':
        return <LaptopIcon />;
      case 'id':
        return <IdIcon />;
      case 'music':
        return <MusicIcon />;
      case 'user':
        return <UserIcon />;
      case 'layout':
        return <LayoutIcon />;
      case 'money':
        return <MoneyIcon />;
      case 'phone':
        return <PhoneIcon />;
      case 'star':
        return <StarIcon />;
      case 'verify':
        return <IdIcon />;
    }
  };

  const handleClick = () => {
    if (achievementKey === 'first-release' && !isDistributionProPlan) {
      dispatch(new OpenMenu('DISTRIBUTION_TRIAL'));
      openDistribution();
      return;
    }
    if (achievementKey === 'verify-artist-profile') {
      dispatch(new OpenMenu('VERIFICATION_MENU'));
      return;
    }
    dispatch(push(to));
  };

  return (
    <motion.div
      className={className || ''}
      onHoverStart={() => setHover(true)}
      onHoverEnd={() => setHover(false)}
    >
      <Wrapper className="link-wrapper" onClick={handleClick}>
        <InfoWrapper className="info">
          <IconWrapper className="icon-wrapper">{getIcon(icon)}</IconWrapper>
          <div className="data">
            <Title customFont="semi-bold">
              {isFrontendCopy ? <Translate id={title} /> : title}
            </Title>
            <Subtitle>
              {isFrontendCopy ? <Translate id={subtitle} /> : subtitle}
            </Subtitle>
          </div>
        </InfoWrapper>
        <AnimatePresence>
          {isMobile && !isCompleted && icon !== 'star' && (
            <CircleRightArrowIcon className="arrow" noHover darkerCircleColor />
          )}
          {isHovered && !isCompleted && icon !== 'star' && (
            <CircleRightArrowIcon className="arrow" noHover isWhite />
          )}
          {isCompleted && icon !== 'star' ? (
            <CompletedIcon className="check" />
          ) : null}
        </AnimatePresence>
      </Wrapper>
    </motion.div>
  );
};

export default connect(mapStateToProps)(styled(AchievementItem)`
  .link-wrapper {
    border-radius: 4px;
    background-color: transparent;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px;
    position: relative;
    transition: background-color
      ${({ theme }) =>
        `${theme.transitions.duration} ${theme.transitions.type}`};

    &:after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      height: 1px;
      background-color: ${({ theme }) => rgba(theme.colors.border, 0.4)};
    }

    @media ${({ theme }) => theme.device.tablet} {
      &:last-child {
        &:after {
          content: none;
        }
      }
    }

    .arrow,
    .check {
      margin-left: 20px;
      flex-shrink: 0;
    }

    ${(props) =>
      props.isCompleted && props.icon !== 'star'
        ? css`
            pointer-events: none;
            .info {
              opacity: 0.5;
            }
          `
        : props.icon !== 'star'
        ? css`
            cursor: pointer;

            &:hover {
              background-color: ${({ theme }) => theme.colors.midGray};

              &:after {
                content: none;
              }

              .icon-wrapper {
                background-color: ${({ theme }) => theme.colors.white};
              }
            }
          `
        : css`
            pointer-events: none;
            .info {
              opacity: 1;
            }
          `}
  }
`);

const IconWrapper = styled.div`
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.lightGray};
  display: flex;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
  margin-right: 20px;
  flex-shrink: 0;
  transition: background-color
    ${({ theme }) => `${theme.transitions.duration} ${theme.transitions.type}`};

  svg {
    width: 16px;
    height: 16px;
  }
`;

const InfoWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: calc(100% - 24px - 20px);

  .data {
    width: 100%;
  }
`;

const Wrapper = styled.div``;

const Title = styled(Typography)`
  margin-bottom: 5px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const Subtitle = styled(Typography)`
  color: ${({ theme }) => theme.colors.tertiary};
  font-size: ${({ theme }) => theme.typography.span};
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;
